import React, { useState, useMemo } from 'react';
import { useTransactions } from '../context/TransactionContext';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../context/ToastContext';
import { QuickExpenseChart } from '../components/DashboardCharts';
import { DashboardCardSkeleton, TransactionSkeleton } from '../components/SkeletonLoader';
import { filterTransactionsByPeriod, getPeriodLabel } from '../utils/dateFilters';


// Quick Add Transaction Component
const QuickAddTransaction = ({ formatCurrency, selectedPeriod }) => {
  const { addTransaction } = useTransactions();
  const { success, error } = useToast();
  const [formData, setFormData] = useState({
    amount: '',
    category: '',
    description: '',
    type: 'expense'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const expenseCategories = ['Groceries', 'Shopping', 'Entertainment', 'Bills & Utilities', 'Rent', 'Transport', 'Food & Dining', 'Healthcare', 'Other'];
  const incomeCategories = ['Salary', 'Freelance', 'Business', 'Investment', 'Other'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.category) {
      error('Please fill all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const amount = parseFloat(formData.amount);
      await addTransaction({
        ...formData,
        amount,
        date: new Date().toISOString().split('T')[0]
      });
      setFormData({ amount: '', category: '', description: '', type: 'expense' });
      success('Transaction added successfully!');
    } catch (err) {
      error('Failed to add transaction');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card">
      <div style={{ marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.25rem' }}>
          Quick Add Transaction
        </h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: 0 }}>
          Add income or expense quickly
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        {/* Type Selection */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, type: 'expense', category: '' }))}
            style={{
              flex: 1,
              padding: '0.625rem',
              borderRadius: '0.375rem',
              border: formData.type === 'expense' ? 'none' : '1px solid var(--gray-300)',
              background: formData.type === 'expense' ? 'var(--black)' : 'var(--gray-100)',
              color: formData.type === 'expense' ? 'white' : 'var(--gray-700)',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
             Expense
          </button>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, type: 'income', category: '' }))}
            style={{
              flex: 1,
              padding: '0.625rem',
              borderRadius: '0.375rem',
              border: formData.type === 'income' ? 'none' : '1px solid var(--gray-300)',
              background: formData.type === 'income' ? 'var(--income-green)' : 'var(--gray-100)',
              color: formData.type === 'income' ? 'white' : 'var(--gray-700)',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
             Income
          </button>
        </div>

        {/* Amount Input */}
        <div style={{ position: 'relative' }}>
          <input
            type="number"
            placeholder="Enter amount"
            value={formData.amount}
            onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
            style={{
              width: '100%',
              padding: '0.75rem',
              paddingLeft: '2.5rem',
              borderRadius: '0.375rem',
              border: '1px solid var(--gray-300)',
              fontSize: '0.875rem',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              fontFamily: 'var(--font-primary)',
              '&:focus': {
                borderColor: 'var(--gray-700)',
                boxShadow: '0 0 0 1px var(--gray-700)'
              }
            }}
            required
          />
          <span style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--gray-500)',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            ₹
          </span>
        </div>

        {/* Category Selection */}
        <select
          value={formData.category}
          onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.375rem',
            border: '1px solid var(--gray-300)',
            fontSize: '0.875rem',
            outline: 'none',
            transition: 'border-color 0.2s',
            background: 'white',
            boxSizing: 'border-box'
          }}
          required
        >
          <option value="">Select Category</option>
          {(formData.type === 'expense' ? expenseCategories : incomeCategories).map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        {/* Description Input */}
        <input
          type="text"
          placeholder="Description (optional)"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.375rem',
            border: '1px solid var(--gray-300)',
            fontSize: '0.875rem',
            outline: 'none',
            transition: 'border-color 0.2s',
            boxSizing: 'border-box'
          }}
        />

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            padding: '0.75rem',
            borderRadius: '0.375rem',
            border: 'none',
            background: formData.type === 'expense' ? 'var(--black)' : 'var(--income-green)',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.7 : 1,
            transition: 'all 0.2s'
          }}
        >
          {isSubmitting ? 'Adding...' : `Add ${formData.type === 'expense' ? 'Expense' : 'Income'}`}
        </button>
      </form>
    </div>
  );
};

function Dashboard() {
  const { transactions, loading } = useTransactions();
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [transactionLimit, setTransactionLimit] = useState(5);
  
  const filteredTransactions = useMemo(() => {
    return filterTransactionsByPeriod(transactions, selectedPeriod);
  }, [transactions, selectedPeriod]);
  
  const stats = useMemo(() => {
    const totalIncome = filteredTransactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
    const totalExpenses = filteredTransactions.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
    const netBalance = totalIncome - totalExpenses;
    return {
      totalBalance: netBalance,
      monthlyIncome: totalIncome,
      monthlyExpenses: totalExpenses,
      savings: netBalance > 0 ? netBalance * 0.2 : 0,
      netBalance
    };
  }, [filteredTransactions]);
  
  const displayName = user?.name || user?.username || 'User';
  
  const cards = [
    { 
      title: 'Total Balance', 
      value: stats.totalBalance, 
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
    },
    { 
      title: 'Monthly Income', 
      value: stats.monthlyIncome, 
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="m16 6 2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg>
    },
    { 
      title: 'Monthly Expenses', 
      value: stats.monthlyExpenses, 
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="m16 18 2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z"/></svg>
    },
    { 
      title: 'Savings', 
      value: stats.savings, 
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M15 10.5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5.67-1.5 1.5-1.5 1.5.67 1.5 1.5zm4-6.5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H5V8h14v10z"/></svg>
    }
  ];



  const recentTransactions = filteredTransactions.slice(0, transactionLimit);

  const getChartData = () => {
    const expenseCategories = {};
    filteredTransactions.forEach(t => {
      if (t.category && t.type === 'expense') {
        expenseCategories[t.category] = (expenseCategories[t.category] || 0) + Math.abs(t.amount);
      }
    });
    return Object.entries(expenseCategories)
      .sort(([,a], [,b]) => b - a) // Sort by amount descending
      .slice(0, 5) // Show top 5 categories
      .map(([name, value]) => ({ name, value }));
  };





  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>
            Dashboard - {displayName}
          </h1>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="form-input"
            style={{ width: 'auto', fontSize: '0.875rem' }}
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="year">This Year</option>
            <option value="all">All Time</option>
          </select>
        </div>
        <p style={{ color: '#6b7280' }}>
          {getPeriodLabel(selectedPeriod)} financial overview
        </p>
      </div>



      <div className="stats-grid">
        {loading && transactions.length === 0 ? (
          [...Array(4)].map((_, i) => <DashboardCardSkeleton key={i} delay={i * 100} />)
        ) : (
          cards.map((card, index) => (
            <div key={index} className="stat-card">
              <div className="stat-header">
                <span className="stat-title">{card.title}</span>
                <span className="stat-icon">{card.icon}</span>
              </div>
              <div className="stat-value">{formatCurrency(card.value)}</div>
              <div className="stat-change positive" style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                {selectedPeriod === 'all' ? 'All time total' : getPeriodLabel(selectedPeriod)}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Dashboard Grid Section */}
      <div className="dashboard-grid" style={{ marginBottom: '2rem' }}>
        {/* Top Expense Categories Chart */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                Top Expense Categories
              </h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', margin: 0 }}>
                {selectedPeriod === 'all' ? 'All time' : getPeriodLabel(selectedPeriod)} spending breakdown
              </p>
            </div>
          </div>
          <div style={{ 
            width: '100%', 
            height: '280px',
            maxWidth: '100%',
            overflow: 'visible',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.5rem',
            background: 'var(--gray-50)',
            borderRadius: '0.5rem',
            border: '1px solid var(--gray-200)',
            margin: '0.5rem 0'
          }}>
            <QuickExpenseChart 
              data={getChartData()} 
              height={260} 
              formatCurrency={formatCurrency} 
            />
          </div>
        </div>

        {/* Quick Add Transaction */}
        <QuickAddTransaction 
          formatCurrency={formatCurrency}
          selectedPeriod={selectedPeriod}
        />
      </div>

      {/* Recent Transactions */}
      <div className="dashboard-grid">

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>Recent Transactions</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <select
                value={transactionLimit}
                onChange={(e) => setTransactionLimit(Number(e.target.value))}
                style={{
                  padding: '0.375rem 0.75rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--gray-300)',
                  fontSize: '0.75rem',
                  outline: 'none',
                  background: 'white',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                <option value={5}>Show 5</option>
                <option value={10}>Show 10</option>
                <option value={25}>Show 25</option>
              </select>
              <a href="/transactions" style={{ color: 'var(--trust-blue)', fontSize: '0.75rem', textDecoration: 'none', fontWeight: '500' }}>
                View All
              </a>
            </div>
          </div>
          <div>
            {loading && transactions.length === 0 ? (
              [...Array(4)].map((_, i) => <TransactionSkeleton key={i} delay={i * 100} />)
            ) : recentTransactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
                <p>No transactions yet</p>
                <p style={{ fontSize: '0.875rem' }}>Add your first transaction to get started</p>
              </div>
            ) : (
              <>
                {recentTransactions.map((transaction) => (
                  <div key={transaction._id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem 0',
                    borderBottom: '1px solid var(--gray-200)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: transaction.amount > 0 ? '#dcfce7' : '#fee2e2'
                      }}>
                        {transaction.amount > 0 ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--income-green)">
                            <path d="M7 14l5-5 5 5z"/>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--expense-red)">
                            <path d="M7 10l5 5 5-5z"/>
                          </svg>
                        )}
                      </div>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                          {transaction.description}
                        </div>
                        <div style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>
                          {transaction.category} {transaction.date}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontWeight: '600',
                      color: transaction.amount > 0 ? 'var(--success-green)' : 'var(--danger-red)'
                    }}>
                      {formatCurrency(transaction.amount)}
                    </div>
                  </div>
                ))}
                {filteredTransactions.length > transactionLimit && (
                  <div style={{ 
                    textAlign: 'center', 
                    paddingTop: '1rem', 
                    borderTop: '1px solid var(--gray-200)',
                    marginTop: '1rem'
                  }}>
                    <p style={{ 
                      color: 'var(--gray-500)', 
                      fontSize: '0.75rem', 
                      margin: 0,
                      fontWeight: '500'
                    }}>
                      Showing {transactionLimit} of {filteredTransactions.length} transactions
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;